import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { logActivity } from '@/lib/api';
import { toast } from 'sonner';
import {
  Download,
  BookOpen,
  Phone,
  GraduationCap,
  Trophy,
  FileText,
  CheckCircle2,
  Zap,
  Target,
  Star,
  Headphones,
  ClipboardCheck,
  Library,
  Search,
  MessageCircle,
  HelpCircle,
  ChevronRight,
  Bell,
  Eye,
  Clock,
  AlertTriangle
} from 'lucide-react';

const quickGuides = [
  {
    title: 'My Calls',
    icon: Phone,
    description: 'View your call history, scores, and detailed analysis',
    tips: [
      'Click on any call to see detailed transcript and scoring',
      'Check the Call Type badge to see what type of call it was',
      'View "N/A" parameters - these don\'t apply to that call type',
      'Use filters to find specific calls by date or type'
    ]
  },
  {
    title: 'QA Notes & Notifications',
    icon: Bell,
    description: 'See feedback from Quality Auditors on your calls',
    tips: [
      'Bell icon shows unread notifications count',
      'Click notification to see the QA note and call',
      'QA Notes appear in the "QA Notes" tab on call detail',
      'Note types: General, Feedback, Action Required, Positive, Needs Improvement'
    ]
  },
  {
    title: 'AI Coaching',
    icon: GraduationCap,
    description: 'Get personalized coaching based on your performance',
    tips: [
      'Review your daily goals and weekly focus areas',
      'Practice skill drills to improve weak areas',
      'Access AI-recommended resources for learning'
    ]
  },
  {
    title: 'Homework AI',
    icon: ClipboardCheck,
    description: 'Complete assigned tasks and get AI feedback',
    tips: [
      'Check Active tab for pending assignments',
      'Submit evidence and notes with your completion',
      'Review AI feedback to understand improvements'
    ]
  },
  {
    title: 'Library',
    icon: Library,
    description: 'Learn from the best call examples',
    tips: [
      'Browse calls by category (Best Rapport, Best Closing, etc.)',
      'Search by situation to find relevant examples',
      'Listen to high-scoring calls for inspiration'
    ]
  },
  {
    title: 'KPI Dashboard',
    icon: Target,
    description: 'Monitor your key performance indicators',
    tips: [
      'Review your monthly KPI breakdown',
      'Understand how each category affects your score',
      'Check tips to improve in weak areas'
    ]
  }
];

const faqItems = [
  {
    question: 'How is my call score calculated?',
    answer: 'Your call score is calculated by AI analyzing multiple areas based on the CALL TYPE. Different call types have different applicable parameters. For example, a Voicemail is scored on 5 parameters while a Submission Call is scored on 15 parameters. Parameters that don\'t apply to your call type are marked "N/A" and excluded from your score.'
  },
  {
    question: 'What are the 18 staffing call types?',
    answer: 'The platform recognizes 18 call types: Job Outreach, Voicemail, Submission Call, Pre-Screening, Spotlight/Discovery Call, Interview Request, Interview Prep, Interview Debrief, Job Offer, Counteroffer Discussion, Salary Negotiation, Onboarding, Placement Check-In, Timesheet, Referral Check, Client Introduction, Follow-Up, and Other.'
  },
  {
    question: 'What are QA Notes?',
    answer: 'QA Notes are feedback comments added by Quality Auditors to your calls. When a QA note is added to one of your calls, you\'ll receive a notification. Note types include: General, Feedback, Action Required, Positive, and Needs Improvement. Use this feedback to improve your performance.'
  },
  {
    question: 'Why do some scoring parameters show "N/A"?',
    answer: 'N/A means "Not Applicable" - the parameter doesn\'t apply to that type of call. For example, RTR Compliance only applies to Submission Calls, and Interview Coaching Quality only applies to Interview Prep calls. This ensures your score is fair based on what\'s relevant.'
  },
  {
    question: 'What are the call classifications?',
    answer: 'Calls are classified into 4 categories based on score: Best (9+), Good (7-8.9), Improvement Needed (5-6.9), and High Alert (below 5). High Alert calls indicate serious quality issues that need immediate attention.'
  },
  {
    question: 'How does AI Coaching work?',
    answer: 'AI Coaching analyzes your recent calls to identify patterns and areas for improvement. It creates a personalized coaching plan with daily goals, skill drills, and recommended resources. The more calls you make, the better the AI understands your strengths and weaknesses.'
  },
  {
    question: 'What happens if I get a QA Note with "Action Required"?',
    answer: 'An "Action Required" QA note means something needs your immediate attention. Review the note carefully, understand the feedback, and make the necessary improvements in your next calls. Your supervisor may also follow up with you.'
  },
  {
    question: 'How often is my KPI updated?',
    answer: 'Your KPI is calculated monthly based on four categories: Call Quality (70%), Homework Completion (10%), Compliance (10%), and Engagement (10%). Scores are updated as new data becomes available throughout the month.'
  },
  {
    question: 'Can the call type be wrong?',
    answer: 'The AI detects call types with high accuracy, but sometimes it may misclassify. If you believe a call type is wrong, contact your supervisor or Quality Auditor - they have the ability to override the detected call type.'
  }
];

export default function UserDocumentation() {
  const context = useOutletContext() || {};
  const { userUrl } = context;
  const [activeTab, setActiveTab] = useState('guides');

  const handleDownload = async () => {
    try {
      if (userUrl) {
        await logActivity('documentation_view', userUrl, { action: 'download' });
      }
      
      const content = generateUserGuide();
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CallSense_User_Guide.md';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('User guide downloaded!');
    } catch (error) {
      toast.error('Failed to download guide');
    }
  };

  const generateUserGuide = () => {
    return `# CallSense - User Guide
## From Feedback to Performance - Updated January 2026

---

## Welcome to CallSense!

This platform helps you improve your call quality through AI-powered analysis, personalized coaching, and comprehensive feedback from Quality Auditors.

---

## What's New

### QA Notes System
- Quality Auditors can now add feedback notes to your calls
- You'll receive notifications when notes are added
- Note types: General, Feedback, Action Required, Positive, Needs Improvement

### 18 Staffing Call Types
The AI now recognizes 18 specific call types:
1. Job Outreach - Initial contact about opportunity
2. Voicemail - Left voice message
3. Submission Call - RTR, detailed Q&A
4. Pre-Screening - Manager reconfirms details
5. Spotlight/Discovery - Hiring manager explains job
6. Interview Request - Informing about interview
7. Interview Prep - Coaching for interview
8. Interview Debrief - Post-interview feedback
9. Job Offer - Extending offer
10. Counteroffer - Competing offers discussion
11. Salary Negotiation - Compensation discussion
12. Onboarding - BGI, drug screening
13. Placement Check-In - Regular check-in
14. Timesheet - Timesheet follow-ups
15. Referral Check - Reference verification
16. Client Introduction - Introducing to client
17. Follow-Up - General follow-up
18. Other - Unclassified

### Scope-Based Scoring
Different call types have different scoring parameters:
- Voicemails: 5 parameters
- Job Outreach: 11 parameters
- Submission Calls: 15 parameters
- Parameters that don't apply show as "N/A"

---

## Quick Navigation

${quickGuides.map(g => `### ${g.title}
${g.description}

**Tips:**
${g.tips.map(t => `- ${t}`).join('\n')}
`).join('\n')}

---

## Frequently Asked Questions

${faqItems.map(f => `### ${f.question}
${f.answer}
`).join('\n')}

---

## Understanding Your Call Score

### How Scores Work
1. AI transcribes and analyzes your call
2. Detects the call type (e.g., Submission Call)
3. Scores only the applicable parameters
4. Non-applicable parameters shown as "N/A"
5. Average calculated from applicable scores only

### Classifications
- **Best (9+)**: Excellent performance
- **Good (7-8.9)**: Meeting expectations
- **Improvement (5-6.9)**: Needs attention
- **High Alert (<5)**: Critical issues

### Fatal Errors
These automatically trigger High Alert:
- Missing company branding (TekWissen)
- Missing quality disclaimer
- Incorrect visa terminology
- DOB/SSN violations

---

## QA Notes - What to Know

### When You Get a QA Note
1. You'll see a notification (bell icon)
2. Click to view the note and call
3. Review the feedback carefully
4. Apply learnings to future calls

### Note Types
- **General**: Standard observation
- **Feedback**: Coaching advice
- **Action Required**: Needs immediate attention!
- **Positive**: Recognition of good work
- **Needs Improvement**: Development area

---

## KPI Scoring

### Categories (Default Weights)
- Call Quality: 70%
- Homework Completion: 10%
- Feedback Compliance: 10%
- Engagement: 10%

### Compliance Penalty
Any compliance violation = 0 KPI for entire month

---

## Need Help?

1. Check the notification bell for QA feedback
2. Use the Support feature to submit a ticket
3. Contact your supervisor directly
4. Check the Library for examples and best practices

---
Generated from CallSense Platform - January 2026
`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Documentation</h1>
          <p className="text-muted-foreground mt-1">
            Learn how to use CallSense effectively - Updated January 2026
          </p>
        </div>
        <Button onClick={handleDownload} className="gap-2">
          <Download className="h-4 w-4" />
          Download Guide
        </Button>
      </div>

      {/* What's New Banner */}
      <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6" />
            <div>
              <h3 className="font-semibold">What&apos;s New in January 2026</h3>
              <p className="text-sm text-blue-100">
                QA Notes System • 18 Staffing Call Types • Scope-Based Scoring • Notifications
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted">
          <TabsTrigger value="guides" className="gap-2">
            <BookOpen className="h-4 w-4" />
            Quick Guides
          </TabsTrigger>
          <TabsTrigger value="calltypes" className="gap-2">
            <Phone className="h-4 w-4" />
            Call Types
          </TabsTrigger>
          <TabsTrigger value="qanotes" className="gap-2">
            <FileText className="h-4 w-4" />
            QA Notes
          </TabsTrigger>
          <TabsTrigger value="faq" className="gap-2">
            <HelpCircle className="h-4 w-4" />
            FAQ
          </TabsTrigger>
        </TabsList>

        {/* Quick Guides Tab */}
        <TabsContent value="guides" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickGuides.map((guide, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <guide.icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{guide.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{guide.description}</p>
                  <div className="space-y-2">
                    {guide.tips.map((tip, tipIndex) => (
                      <div key={tipIndex} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-foreground">{tip}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Call Types Tab */}
        <TabsContent value="calltypes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-500" />
                18 Staffing Call Types
              </CardTitle>
              <CardDescription>
                Each call type has different scoring parameters - Non-applicable parameters show as &quot;N/A&quot;
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { name: 'Job Outreach', desc: 'Initial contact about job opportunity', params: 11 },
                    { name: 'Voicemail', desc: 'Left voice message', params: 5 },
                    { name: 'Submission Call', desc: 'RTR, detailed Q&A, next steps', params: 15 },
                    { name: 'Pre-Screening', desc: 'Manager reconfirms details', params: 13 },
                    { name: 'Spotlight/Discovery', desc: 'Hiring manager explains job', params: 11, isNew: true },
                    { name: 'Interview Request', desc: 'Informing about interview', params: 10 },
                    { name: 'Interview Prep', desc: 'Coaching for interview', params: 11 },
                    { name: 'Interview Debrief', desc: 'Post-interview feedback', params: 9, isNew: true },
                    { name: 'Job Offer', desc: 'Extending offer', params: 10 },
                    { name: 'Counteroffer', desc: 'Competing offers', params: 9, isNew: true },
                    { name: 'Salary Negotiation', desc: 'Compensation discussion', params: 11, isNew: true },
                    { name: 'Onboarding', desc: 'BGI, drug screening', params: 8 },
                    { name: 'Placement Check-In', desc: 'Check-in with placed', params: 9, isNew: true },
                    { name: 'Timesheet', desc: 'Timesheet follow-ups', params: 4 },
                    { name: 'Referral Check', desc: 'Reference verification', params: 6 },
                    { name: 'Client Introduction', desc: 'Introducing to client', params: 10, isNew: true },
                    { name: 'Follow-Up', desc: 'General follow-up', params: 8 },
                    { name: 'Other', desc: 'Unclassified calls', params: 9 }
                  ].map((type, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${type.isNew ? 'border-yellow-300 bg-yellow-50' : 'border-border'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{type.name}</span>
                        {type.isNew && <Badge className="bg-yellow-100 text-yellow-700 text-xs">NEW</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{type.desc}</p>
                      <Badge variant="outline" className="mt-2 text-xs">{type.params} scoring params</Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* QA Notes Tab */}
        <TabsContent value="qanotes" className="mt-6">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-rose-500" />
                  QA Notes & Notifications
                </CardTitle>
                <CardDescription>
                  Quality Auditors add feedback to help you improve
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">How It Works</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-blue-600">1.</span>
                        QA Auditor reviews your call
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-blue-600">2.</span>
                        They add a note with feedback
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-blue-600">3.</span>
                        You get a notification (bell icon)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-blue-600">4.</span>
                        Click to view note and call
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Note Types</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-muted text-foreground">General</Badge>
                        <span className="text-sm text-muted-foreground">Standard observation</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700">Feedback</Badge>
                        <span className="text-sm text-muted-foreground">Coaching advice</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-700">Action Required</Badge>
                        <span className="text-sm text-muted-foreground">Needs attention!</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Positive</Badge>
                        <span className="text-sm text-muted-foreground">Recognition</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-100 text-amber-700">Needs Improvement</Badge>
                        <span className="text-sm text-muted-foreground">Development area</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-800">Action Required Notes</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        If you receive an &quot;Action Required&quot; note, review it immediately and make the necessary 
                        improvements. Your supervisor may follow up with you about these notes.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-purple-500" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-4">
                  {faqItems.map((faq, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-foreground flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-blue-500" />
                        {faq.question}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Need Help Card */}
      <Card className="bg-muted/50">
        <CardContent className="p-6 text-center">
          <p className="text-foreground font-medium">Need more help?</p>
          <p className="text-muted-foreground text-sm mt-1">
            Check the notification bell for QA feedback, contact your supervisor, or explore the Call Library for examples.
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            © 2026 CallSense - From Feedback to Performance
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
